import { Controller } from "@hotwired/stimulus"

// The assistant's side of the preview (ASS-37 to ASS-44).
//
// It talks to the frame through postMessage and through nothing else: the frame
// is sandboxed without allow-same-origin, so there is no reaching into it
// (ASS-46). Every message coming back is treated as untrusted — template script
// sits in the same document (ABS-3, ASS-48) — so only known placeholder keys of
// this step are ever acted on.
//
// If any of it fails, the fields, the saving and the steps carry on unchanged
// (ASS-6, ASS-45).
// Where the fold of ASS-32 is remembered. It is a way of looking at the page
// rather than anything about one spot, so it is not keyed by one.
const FOLDED = "assistant:preview-folded"

// How much of the window has to disappear before it is taken for a keyboard.
// The URL bar of a phone browser sliding away is worth some 60 px; every
// on-screen keyboard is worth three times that.
const KEYBOARD = 150

export default class extends Controller {
  static targets = [
    "frame", "stage", "overlay", "marksButton", "landscapeButton", "portraitButton",
    "preview", "foldButton"
  ]
  static values = {
    seconds: Number, // ASS-13 — the moment this step's scene stands at
    keys: Array // the placeholders of this step
  }

  connect() {
    this.marks = false
    this.pinned = false
    this.known = new Set()
    this.listen = this.receive.bind(this)
    this.relayout = this.measure.bind(this)
    this.viewport = this.keyboard.bind(this)
    window.addEventListener("message", this.listen)
    window.addEventListener("resize", this.relayout)
    window.visualViewport?.addEventListener("resize", this.viewport)
    this.element.addEventListener("focusin", this.focused.bind(this))
    this.element.addEventListener("focusout", this.blurred.bind(this))
    this.restoreFold()
    this.railToActive()
    this.measure()
  }

  disconnect() {
    window.removeEventListener("message", this.listen)
    window.removeEventListener("resize", this.relayout)
    window.visualViewport?.removeEventListener("resize", this.viewport)
  }

  // --- the room the preview takes (ASS-32, ASS-33) ---------------------------

  // The pane stands over the page, and the browser scrolling a focused field
  // into view knows nothing about it. This is the number that tells the CSS how
  // far down "the top" really is; it changes with the format, with the fold and
  // with the keyboard, so it is measured rather than written down.
  measure() {
    if (!this.hasPreviewTarget) return

    const navbar = document.querySelector(".navbar")?.getBoundingClientRect().height || 0
    const preview = this.previewTarget.getBoundingClientRect().height

    this.element.style.setProperty("--assistant-sticky", `${Math.round(navbar + preview)}px`)
  }

  // ASS-32 — "in verkleinerter Höhe, wie ein Videofenster". The keyboard is up,
  // so the picture makes way for the field rather than going: what is being
  // typed still has to be visible while it is typed (ASS-37).
  keyboard() {
    const hidden = window.innerHeight - window.visualViewport.height

    this.previewTarget?.classList.toggle("assistant__preview--tight", hidden > KEYBOARD)
    requestAnimationFrame(() => this.measure())
  }

  toggleFold() {
    this.setFold(!this.previewTarget.classList.contains("assistant__preview--folded"))
  }

  setFold(folded) {
    this.previewTarget.classList.toggle("assistant__preview--folded", folded)

    if (this.hasFoldButtonTarget) {
      const button = this.foldButtonTarget
      button.textContent = folded ? button.dataset.unfold : button.dataset.fold
      button.setAttribute("aria-expanded", String(!folded))
    }

    // A browser with no storage at all — private mode on some phones — must not
    // cost anybody the fold; it simply forgets it between pages.
    try {
      window.localStorage.setItem(FOLDED, folded ? "1" : "")
    } catch {
      /* nothing to remember it with */
    }

    requestAnimationFrame(() => this.measure())
  }

  restoreFold() {
    if (!this.hasPreviewTarget) return

    let folded = false
    try {
      folded = window.localStorage.getItem(FOLDED) === "1"
    } catch {
      /* nothing to remember it with */
    }

    if (folded) this.setFold(true)
  }

  // ASS-25 — the rail says how far along somebody is, and it says nothing at all
  // when the open step sits off the right edge of it. Only the rail's own scroll
  // is touched, never the page's.
  railToActive() {
    const rail = this.element.querySelector(".assistant__rail")
    const active = rail?.querySelector(".nav-link.active")
    if (!rail || !active) return

    const box = rail.getBoundingClientRect()
    const pill = active.getBoundingClientRect()

    rail.scrollLeft += pill.left - box.left - (box.width - pill.width) / 2
  }

  // --- from the preview -----------------------------------------------------

  receive(event) {
    if (event.source !== this.frameTarget.contentWindow) return

    const message = event.data
    if (!message || message.source !== "loc-preview") return

    if (message.type === "ready") this.ready(message)
    else if (message.type === "click") this.clicked(message.key)
    else if (message.type === "rects") this.draw(message)
    else if (message.type === "overflow") this.overflow(message)
  }

  ready(message) {
    // ASS-48 — the keys are checked against what this step actually has before
    // any of them is trusted with anything.
    this.known = new Set(message.keys.filter((key) => /^loc\d{3}$/.test(key)))
    this.seek()
    this.patchAll()
    // ASS-39 — a short look at what can be changed here, then out of the way.
    this.showMarks()
    this.marksTimer = setTimeout(() => this.hideMarks(), 2500)
  }

  clicked(key) {
    if (!this.known.has(key) || !this.keysValue.includes(key)) return

    const field = this.fieldFor(key)
    if (!field) return

    // ASS-33 — on a phone the fields are below the preview, so this is the
    // scroll that makes the tap useful at all.
    //
    // `start` and not `center`: centring ignores `scroll-margin-top`, which is
    // the only thing that knows the sticky preview is standing over the top of
    // the page — a centred field on a short screen lands behind the picture it
    // was just tapped in.
    field.scrollIntoView({ behavior: "smooth", block: "start" })
    field.querySelector("input, textarea, select, button")?.focus({ preventScroll: true })
    field.classList.add("assistant__field--touched")
    setTimeout(() => field.classList.remove("assistant__field--touched"), 1200)
  }

  // ASS-42 — a text that no longer fits is a warning at its field, never a
  // block: what a template does with too much text is a design question.
  overflow(message) {
    const field = this.fieldFor(message.key)
    if (!field) return

    field.querySelector("[data-overflow]")?.classList.toggle("d-none", !message.overflowing)
  }

  // --- to the preview -------------------------------------------------------

  post(message) {
    this.frameTarget.contentWindow?.postMessage({ source: "loc-assistant", ...message }, "*")
  }

  seek() {
    this.post({ type: "seek", seconds: this.hasSecondsValue ? this.secondsValue : null })
  }

  play() {
    this.post({ type: "play" })
  }

  // ASS-37 — every field of this step, once, so a preview that has just loaded
  // shows what is already typed rather than the saved state.
  patchAll() {
    this.fields().forEach((field) => this.patch(field))
  }

  patch(field) {
    const key = field.dataset.slotKey
    if (!this.known.has(key)) return

    const value = this.valueOf(field)
    if (value === null) return

    this.post({ type: "patch", key, value: value.text, format: value.format })
  }

  valueOf(field) {
    const editor = field.querySelector("trix-editor")
    if (editor) return { text: editor.value, format: "rich" }

    const input = field.querySelector("textarea:not([hidden]), input[type=text]")
    if (input) return { text: input.value, format: "plain" }

    const image = field.querySelector("[data-media-picker-target=image]")
    if (image && !image.classList.contains("d-none")) return { text: image.src, format: "media" }

    return null
  }

  // ASS-34 — a responsive template is turned rather than re-rendered: the
  // design carries both formats (VOR-67), and what changes is the surface it is
  // given.
  landscape() {
    this.format("16 / 9", 16 / 9, this.landscapeButtonTarget, this.portraitButtonTarget)
  }

  portrait() {
    this.format("9 / 16", 9 / 16, this.portraitButtonTarget, this.landscapeButtonTarget)
  }

  // Both, and for the same reason as in the view: the shape and the number the
  // pane's width is worked out from (ASS-34).
  format(ratio, factor, pressed, released) {
    this.stageTarget.style.aspectRatio = ratio
    this.stageTarget.style.setProperty("--assistant-ratio", factor.toFixed(4))
    pressed.classList.add("active")
    released.classList.remove("active")
    this.redraw()
    // Turning the pane changes how much of the screen it stands on, and that is
    // the number every field's scroll position is worked out from.
    this.measure()
  }

  // --- marking (ASS-39, ASS-40) ---------------------------------------------

  toggleMarks() {
    this.pinned ? this.hideMarks() : this.showMarks()
  }

  // The button asks for all of them and keeps them until it is pressed again —
  // `pinned` is that request, and it is what the focus marking has to fall back
  // to rather than simply switch off (ASS-39).
  showMarks() {
    clearTimeout(this.marksTimer)
    this.pinned = true
    this.marks = true
    this.marksButtonTarget?.classList.add("active")
    this.post({ type: "rects", keys: this.keysValue })
  }

  // Not `clearMarks` but `redraw`: the short look of ASS-39 takes itself back
  // after two and a half seconds, and by then the editor may already be in a
  // field — whose marking is the one thing that survives this.
  hideMarks() {
    this.pinned = false
    this.marksButtonTarget?.classList.remove("active")
    this.redraw()
  }

  clearMarks() {
    this.marks = false
    this.overlayTarget.replaceChildren()
  }

  // Whatever the marking should be showing right now: the one focused field,
  // otherwise the whole step where the button asked for it, otherwise nothing.
  // Everything that changes the picture goes through here, so no drawing ever
  // outlives its reason.
  redraw() {
    const field = this.focusedField()

    if (field) this.post({ type: "rects", keys: [field.dataset.slotKey] })
    else if (this.pinned) this.post({ type: "rects", keys: this.keysValue })
    else this.clearMarks()
  }

  // The boxes come from inside and are drawn out here, over the frame, so the
  // template's own document stays exactly as a screen would show it (ASS-40).
  draw(message) {
    if (!this.marks) return

    // The frame is scaled as a whole, so its rendered box is already the
    // stage — the boxes only have to be brought from the screen's own pixels
    // into the same scale (ASS-40).
    const frame = this.frameTarget.getBoundingClientRect()
    const scaleX = frame.width / (message.width || frame.width)
    const scaleY = frame.height / (message.height || frame.height)

    this.overlayTarget.replaceChildren(
      ...message.boxes.map((box) => {
        const mark = document.createElement("div")
        mark.className = "assistant__mark"
        mark.style.top = `${box.top * scaleY}px`
        mark.style.left = `${box.left * scaleX}px`
        mark.style.width = `${box.width * scaleX}px`
        mark.style.height = `${box.height * scaleY}px`
        return mark
      })
    )
  }

  // --- fields ---------------------------------------------------------------

  // ASS-38 in the other direction: what has the focus is marked in the picture.
  focused(event) {
    const field = event.target.closest("[data-slot-key]")
    if (!field) return

    this.marks = true
    this.post({ type: "rects", keys: [field.dataset.slotKey] })
  }

  // ... and only for as long as it has it. A marking left standing after the
  // focus has gone points at a field nobody is editing, which is worse than no
  // marking at all.
  //
  // `relatedTarget` is where the focus is going, and it is read rather than
  // `document.activeElement`, which is still the body while this fires. A move
  // from one field to the next therefore does nothing here — the `focusin`
  // that follows draws the new one.
  blurred(event) {
    const next = event.relatedTarget
    if (next && this.element.contains(next) && next.closest("[data-slot-key]")) return

    this.redraw()
  }

  // The frame counts as nothing focused: with focus inside it `activeElement`
  // is the frame element, which sits in no field.
  focusedField() {
    const active = document.activeElement

    return active && this.element.contains(active) ? active.closest("[data-slot-key]") : null
  }

  // ASS-37 — typing shows up in the picture. The event comes from the form
  // controller next door, which is also the one that saves.
  changed(event) {
    const field = event.target.closest("[data-slot-key]")
    if (field) this.patch(field)
  }

  fields() {
    return Array.from(this.element.querySelectorAll("[data-slot-key]"))
  }

  fieldFor(key) {
    return this.element.querySelector(`[data-slot-key="${CSS.escape(key)}"]`)
  }
}
